import { apiClient } from '../api';

// ========== TYPES ==========

export interface City {
  id: number;
  name: string;
}

export interface DeliveryZone {
  id: number;
  polygon: number[][][];
  delivery_cost: number;
  min_order_sum: number;
  estimated_time: string;
}

export interface DeliveryPoint {
  id: number;
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  working_hours: string;
  is_active: boolean;
  technical_break: boolean;
  city_id: number;
  zones: DeliveryZone[];
}

export interface AddressSuggestion {
  value: string;
  unrestricted_value: string;
  data: {
    postal_code?: string;
    country?: string;
    region?: string;
    city?: string;
    street?: string;
    house?: string;
    flat?: string;
    geo_lat?: string;
    geo_lon?: string;
  };
}

export interface DeliveryCheckResult {
  isInZone: boolean;
  zone?: DeliveryZone;
  deliveryPointId?: number;
  deliveryCost?: number;
  estimatedTime?: string;
  minOrderSum?: number;
  nearestPoint?: {
    id: number;
    distance: number;
  };
  message?: string;
  error?: string;
}

// ========== API FUNCTIONS ==========

export const deliveryApi = {
  // Получить список городов
  getCities: async (): Promise<City[]> => {
    return apiClient.get<City[]>('/cities');
  },

  // Получить точки доставки для города
  getDeliveryPoints: async (cityId: number): Promise<DeliveryPoint[]> => {
    return apiClient.get<DeliveryPoint[]>(`/deliveryclients/delivery-points/${cityId}`);
  },

  // Автокомплит адреса через DaData
  autocomplete: async (query: string, city?: string): Promise<{ suggestions: AddressSuggestion[] }> => {
    return apiClient.get<{ suggestions: AddressSuggestion[] }>('/address/autocomplete', { 
      query, 
      city 
    });
  },

  // Проверить адрес на попадание в зону доставки
  checkDeliveryZone: async (address: string, cityId: number, coordinates?: { lat: number; lon: number }): Promise<DeliveryCheckResult> => {
    return apiClient.post<DeliveryCheckResult>('/deliveryclients/check-zone', {
      address,
      cityId,
      ...(coordinates && { lat: coordinates.lat, lon: coordinates.lon }),
    });
  },

  // Геокодирование адреса (получение координат)
  geocode: async (address: string): Promise<{ lat: number; lon: number } | null> => {
    try {
      const result = await apiClient.post<{ suggestions: AddressSuggestion[] }>('/address/suggest', { 
        query: address 
      });
      
      if (result.suggestions?.[0]?.data?.geo_lat && result.suggestions?.[0]?.data?.geo_lon) {
        return {
          lat: parseFloat(result.suggestions[0].data.geo_lat),
          lon: parseFloat(result.suggestions[0].data.geo_lon),
        };
      }
      return null;
    } catch {
      return null;
    }
  },

  // Reverse geocode — получить адрес по координатам
  reverseGeocode: async (lat: number, lon: number): Promise<{ address: string; data: AddressSuggestion['data'] | null }> => {
    return apiClient.get<{ address: string; data: AddressSuggestion['data'] | null }>('/address/reverse-geocode', {
      lat: String(lat),
      lon: String(lon),
    });
  },
};


